/**
 * Render M'Dhoria mud text as safe HTML.
 *
 * Handles:
 *  - ANSI SGR from the mud colour() converter (e.g. \x1B[1;36m)
 *  - Brace codes {R {x etc. if they appear raw
 *  - XSS: all literal text is escaped
 */

/** CSS class for a foreground ANSI color (0–7 = normal, 8–15 = bright). */
const FG_CLASS: Record<number, string> = {
  0: "fg-black",
  1: "fg-red",
  2: "fg-green",
  3: "fg-yellow",
  4: "fg-blue",
  5: "fg-magenta",
  6: "fg-cyan",
  7: "fg-white",
  8: "fg-bright-black",
  9: "fg-bright-red",
  10: "fg-bright-green",
  11: "fg-bright-yellow",
  12: "fg-bright-blue",
  13: "fg-bright-magenta",
  14: "fg-bright-cyan",
  15: "fg-bright-white",
};

/** Brace color codes used in source (same mapping as descriptor::colour). */
const BRACE_TO_FG: Record<string, { fg: number; bold: boolean }> = {
  x: { fg: 7, bold: false }, // reset → default white-ish
  X: { fg: 7, bold: false },
  r: { fg: 1, bold: false },
  g: { fg: 2, bold: false },
  y: { fg: 3, bold: false },
  b: { fg: 4, bold: false },
  m: { fg: 5, bold: false },
  c: { fg: 6, bold: false },
  w: { fg: 7, bold: false },
  R: { fg: 9, bold: true },
  G: { fg: 10, bold: true },
  Y: { fg: 11, bold: true },
  B: { fg: 12, bold: true },
  M: { fg: 13, bold: true },
  C: { fg: 14, bold: true },
  W: { fg: 15, bold: true },
  D: { fg: 8, bold: true }, // dark grey = bold black
  d: { fg: 8, bold: false },
};

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Mud historically emits LF+CR (\n\r). Treating \r alone as newline after
 * normalizing \r\n would turn \n\r into \n\n (double spacing).
 */
export function normalizeNewlines(text: string): string {
  return text
    .replace(/\n\r/g, "\n")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/\u0007/g, ""); // BEL
}

interface Style {
  fg: number | null; // 0–15 or null = default
  bold: boolean;
  dim: boolean;
  underline: boolean;
  reverse: boolean;
}

function defaultStyle(): Style {
  return { fg: null, bold: false, dim: false, underline: false, reverse: false };
}

function styleClass(s: Style): string {
  const parts: string[] = [];
  if (s.fg !== null && FG_CLASS[s.fg]) parts.push(FG_CLASS[s.fg]!);
  if (s.bold) parts.push("s-bold");
  if (s.dim) parts.push("s-dim");
  if (s.underline) parts.push("s-underline");
  if (s.reverse) parts.push("s-reverse");
  return parts.join(" ");
}

function applySgr(style: Style, params: number[]): Style {
  const next = { ...style };
  if (params.length === 0) params = [0];

  for (const p of params) {
    if (p === 0) {
      Object.assign(next, defaultStyle());
    } else if (p === 1) {
      next.bold = true;
    } else if (p === 2) {
      next.dim = true;
    } else if (p === 4) {
      next.underline = true;
    } else if (p === 7) {
      next.reverse = true;
    } else if (p === 22) {
      next.bold = false;
      next.dim = false;
    } else if (p === 24) {
      next.underline = false;
    } else if (p === 27) {
      next.reverse = false;
    } else if (p === 39) {
      next.fg = null;
    } else if (p >= 30 && p <= 37) {
      // With bold already set, many clients show bright; mud uses 1;3Xm for bright.
      next.fg = next.bold ? p - 30 + 8 : p - 30;
    } else if (p >= 90 && p <= 97) {
      next.fg = p - 90 + 8;
    }
    // ignore background 40–47, 100–107 for now
  }

  // Mud bright: "\x1B[1;36m" → params [1, 36]. After 1 bold=true, after 36
  // we set fg with bold → 14. Good.
  // "\x1B[0;31m" → reset then red normal. Good.
  // "\x1B[1;30m" → bold black → fg 8. Good.
  return next;
}

/**
 * Convert mud stream text (ANSI and/or brace codes) to HTML.
 */
export function mudTextToHtml(raw: string): string {
  const text = normalizeNewlines(raw);
  let html = "";
  let i = 0;
  let style = defaultStyle();
  let openClass = "";

  const flushOpen = (cls: string) => {
    if (cls !== openClass) {
      if (openClass) html += "</span>";
      if (cls) html += `<span class="${cls}">`;
      openClass = cls;
    }
  };

  const closeAll = () => {
    if (openClass) {
      html += "</span>";
      openClass = "";
    }
  };

  while (i < text.length) {
    const ch = text[i]!;

    // ANSI CSI: ESC [ ... final-byte
    if (ch === "\u001b" && text[i + 1] === "[") {
      let j = i + 2;
      while (j < text.length) {
        const c = text[j]!;
        if (c >= "\x40" && c <= "\x7e") break; // final byte
        j++;
      }
      if (j >= text.length) {
        // Incomplete sequence — drop rest (rare on WS frame boundary)
        break;
      }
      const final = text[j]!;
      const body = text.slice(i + 2, j);
      if (final === "m") {
        const params = body
          .split(";")
          .filter((x) => x.length > 0)
          .map((x) => Number.parseInt(x, 10))
          .filter((n) => Number.isFinite(n));
        style = applySgr(style, params.length ? params : [0]);
        flushOpen(styleClass(style));
      }
      // ignore other CSI (cursor, clear, etc.)
      i = j + 1;
      continue;
    }

    // Brace color {X
    if (ch === "{" && i + 1 < text.length) {
      const code = text[i + 1]!;
      if (code === "{") {
        flushOpen(styleClass(style));
        html += "{";
        i += 2;
        continue;
      }
      if (code === "*") {
        // mud bell — skip
        i += 2;
        continue;
      }
      const mapped = BRACE_TO_FG[code];
      if (mapped) {
        if (code === "x" || code === "X") {
          style = defaultStyle();
        } else {
          style = {
            ...defaultStyle(),
            fg: mapped.fg,
            bold: mapped.bold,
          };
        }
        flushOpen(styleClass(style));
        i += 2;
        continue;
      }
    }

    flushOpen(styleClass(style));
    html += escapeHtml(ch);
    i++;
  }

  closeAll();
  return html;
}
